import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Bell, Menu, X, ChevronDown, ChevronRight, ChevronUp, Upload, Check, Clock, Play, Pause,
  Download, AlertCircle, Wifi, Mail, Phone, MapPin, Camera, Video, 
  Users, Settings, LogOut, Eye, EyeOff, Trash2, RefreshCw, Star, Heart, Home,
  ArrowRight, Shield, Award, Zap, FileDown, GripVertical, Plus, Film,
  FileSpreadsheet, BarChart3, Package, ArrowLeft, Sparkles, Gift,
  Volume2, VolumeX, BookOpen, Truck, Monitor, Edit3, Share2, Info, History
} from "lucide-react";
import AdminPageComponent from "./pages/AdminPage";
import RealEstateCheckoutPageComponent from "./pages/RealEstateCheckoutPage";
import CheckoutPageComponent from "./pages/CheckoutPage";
import DashboardPageComponent from "./pages/DashboardPage";
import OrderDetailPageComponent from "./pages/OrderDetailPage";
import { REHeroVideoPlayer, REMidVideoPlayer, REDemoVideoPlayer } from "./components/REVideoPlayers";
import { MemorialHeroVideoPlayer, MemorialDemoVideoPlayer } from "./components/MemorialVideoPlayers";
import MemorialMomentsCarousel from "./components/MemorialMomentsCarousel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== BACK BUTTON COMPONENT ==============
const BackButton = ({ to, label = "Back" }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => to ? navigate(to) : navigate(-1)}
      className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-500 transition-colors mb-6 group"
      data-testid="back-button"
    >
      <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
      <span>{label}</span>
    </button>
  );
};

// ============== CONTEXT ==============
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle auth state on token change
    const initAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
      } catch (e) {
        localStorage.removeItem("token");
        setToken(null);
        delete axios.defaults.headers.common["Authorization"];
      }
      setLoading(false);
    };
    
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const signup = async (data) => {
    const res = await axios.post(`${API}/auth/signup`, data);
    localStorage.setItem("token", res.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Wrapper for AdminPage to pass useAuth hook
const AdminPage = () => <AdminPageComponent useAuth={useAuth} />;

// Wrapper for RealEstateCheckoutPage to pass useAuth hook
const RealEstateCheckoutPage = () => <RealEstateCheckoutPageComponent useAuth={useAuth} />;

// Wrapper for CheckoutPage to pass useAuth hook
const CheckoutPage = () => <CheckoutPageComponent useAuth={useAuth} />;

// Wrapper for DashboardPage to pass useAuth hook
const DashboardPage = () => <DashboardPageComponent useAuth={useAuth} />;

// Wrapper for OrderDetailPage to pass useAuth hook
const OrderDetailPage = () => <OrderDetailPageComponent useAuth={useAuth} />;

// Vertical guard — redirects customers away from wrong vertical's checkout
const VerticalGuard = ({ allowedVertical, children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (user && user.role === "customer" && user.vertical && user.vertical !== "all" && user.vertical !== allowedVertical) {
      navigate(user.vertical === "real_estate" ? "/real-estate" : "/memorial", { replace: true });
    }
  }, [user, allowedVertical, navigate]);
  return children;
};

// ============== COMPONENTS ==============
const Navbar = () => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Determine if we're in real estate section (check both path and redirect param)
  const redirectTo = searchParams.get("redirect") || "";
  const isRealEstate = location.pathname.startsWith('/real-estate') || redirectTo.includes('real-estate');
  const accentColor = isRealEstate ? 'emerald' : 'yellow';

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.notifications || []);
    } catch (e) {
      console.error("Failed to fetch notifications:", e);
    }
  };

  useEffect(() => {
    if (user) {
      // Fetch notifications when user changes
      (async () => {
        try {
          const res = await axios.get(`${API}/notifications`);
          setNotifications(res.data.notifications || []);
        } catch (e) {
          console.error("Failed to fetch notifications:", e);
        }
      })();
    }
  }, [user]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error("Failed to mark notifications as read:", e);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-zinc-800" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            {isRealEstate ? (
              <span className="text-2xl font-bold text-emerald-500 font-serif">Still 2 Move</span>
            ) : (
              <span className="text-2xl font-bold text-yellow-500 font-serif">Still 2 Move</span>
            )}
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-zinc-300 hover:text-white transition">Home</Link>
            <Link 
              to="/faq" 
              state={{ from: isRealEstate ? "real_estate" : "memorial" }}
              className="text-zinc-300 hover:text-white transition"
            >FAQ</Link>
            {user ? (
              <>
                <Link to="/dashboard" className="text-zinc-300 hover:text-white transition">Dashboard</Link>
                {user.role === "admin" && (
                  <Link to="/admin" className="text-zinc-300 hover:text-white transition">Admin</Link>
                )}
                <div className="relative">
                  <button 
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 text-zinc-300 hover:text-white"
                    data-testid="notifications-btn"
                  >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 text-black text-xs rounded-full flex items-center justify-center font-bold">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl" data-testid="notifications-panel">
                      <div className="flex justify-between items-center p-4 border-b border-zinc-800">
                        <span className="font-semibold">Notifications</span>
                        <button onClick={markAllRead} className="text-xs text-yellow-500 hover:text-yellow-400">Mark all read</button>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="p-4 text-zinc-500 text-center">No notifications</p>
                        ) : (
                          notifications.slice(0, 10).map(n => (
                            <div key={n.id} className={`p-4 border-b border-zinc-800 ${!n.read ? 'bg-zinc-800/50' : ''}`}>
                              <p className="font-medium text-sm">{n.title}</p>
                              <p className="text-xs text-zinc-400 mt-1">{n.message}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={logout} className="text-zinc-300 hover:text-white flex items-center gap-2" data-testid="logout-btn">
                  <LogOut size={18} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-zinc-300 hover:text-white transition" data-testid="login-link">Login</Link>
                <Link 
                  to={isRealEstate ? "/real-estate/checkout" : "/checkout"} 
                  className={`${isRealEstate ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-yellow-500 hover:bg-yellow-400'} text-black px-4 py-2 rounded-lg font-semibold transition`}
                  data-testid="signup-link"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-zinc-800">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-zinc-300 hover:text-white" onClick={() => setMenuOpen(false)}>Home</Link>
              <Link 
                to="/faq" 
                state={{ from: isRealEstate ? "real_estate" : "memorial" }}
                className="text-zinc-300 hover:text-white" 
                onClick={() => setMenuOpen(false)}
              >FAQ</Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="text-zinc-300 hover:text-white" onClick={() => setMenuOpen(false)}>Dashboard</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" className="text-zinc-300 hover:text-white" onClick={() => setMenuOpen(false)}>Admin</Link>
                  )}
                  <button onClick={() => { logout(); setMenuOpen(false); }} className="text-zinc-300 hover:text-white text-left">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-zinc-300 hover:text-white" onClick={() => setMenuOpen(false)}>Login</Link>
                  <Link 
                    to={isRealEstate ? "/real-estate/checkout" : "/checkout"} 
                    className={`${isRealEstate ? 'bg-emerald-500 hover:bg-emerald-400' : 'bg-yellow-500 hover:bg-yellow-400'} text-black px-4 py-2 rounded-lg font-semibold text-center`}
                    onClick={() => setMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const Footer = () => {
  const [supportEmail, setSupportEmail] = useState("izzopro@izzopro.com");
  const location = useLocation();
  const isRealEstate = location.pathname.startsWith('/real-estate');
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/public`);
        if (res.data.support_email) {
          setSupportEmail(res.data.support_email);
        }
      } catch (e) {
        // Use default
      }
    };
    fetchSettings();
  }, []);

  return (
    <footer className="bg-zinc-950 border-t border-zinc-800 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            {isRealEstate ? (
              <>
                <h3 className="text-xl font-bold text-emerald-500 font-serif mb-4">Still 2 Move</h3>
                <p className="text-zinc-400 text-sm">Creating stunning property videos that help real estate professionals sell faster.</p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-yellow-500 font-serif mb-4">Still 2 Move</h3>
                <p className="text-zinc-400 text-sm">Bringing your photos to life through cinematic video.</p>
              </>
            )}
          </div>
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-zinc-400 hover:text-white text-sm">Home</Link>
              <Link to="/faq" className="text-zinc-400 hover:text-white text-sm">FAQ</Link>
              <Link to="/terms" className="text-zinc-400 hover:text-white text-sm">Terms of Service</Link>
              <Link to="/privacy" className="text-zinc-400 hover:text-white text-sm">Privacy Policy</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <div className="flex flex-col gap-2 text-zinc-400 text-sm">
              <a href={`mailto:${supportEmail}`} className="flex items-center gap-2 hover:text-white">
                <Mail size={16} /> {supportEmail}
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Hours</h4>
            <p className="text-zinc-400 text-sm">Monday - Friday: 9am - 6pm EST</p>
            <p className="text-zinc-400 text-sm mt-1">Weekend: By appointment</p>
          </div>
        </div>
        <div className="border-t border-zinc-800 mt-8 pt-8 text-center text-zinc-500 text-sm">
          © {new Date().getFullYear()} Still 2 Move. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

const StatusBadge = ({ status }) => {
  const statusMap = {
    pending_payment: { label: "Pending Payment", class: "status-pending" },
    awaiting_upload: { label: "Awaiting Upload", class: "status-awaiting" },
    in_progress: { label: "In Progress", class: "status-progress" },
    review: { label: "Ready for Review", class: "status-review" },
    completed: { label: "Completed", class: "status-completed" },
  };
  const s = statusMap[status] || { label: status, class: "status-pending" };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${s.class}`} data-testid="status-badge">
      {s.label}
    </span>
  );
};

const RushBadge = () => (
  <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-500 text-white animate-pulse flex items-center gap-1" data-testid="rush-badge">
    <Zap size={12} /> RUSH
  </span>
);

const VideoBookBadge = () => (
  <span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-500 border border-yellow-500/50 flex items-center gap-1" data-testid="videobook-badge">
    <BookOpen size={12} /> VIDEO BOOK
  </span>
);

const VerticalBadge = ({ vertical }) => {
  if (vertical === "real_estate") {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-500 border border-emerald-500/50 flex items-center gap-1" data-testid="vertical-badge-re">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
        REAL ESTATE
      </span>
    );
  }
  return (
    <span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-500 border border-yellow-500/50 flex items-center gap-1" data-testid="vertical-badge-memorial">
      <Heart size={12} /> MEMORIAL
    </span>
  );
};

const RushDeadlineCountdown = ({ timeline }) => {
  if (!timeline?.is_rush || !timeline?.rush_deadline) return null;
  
  const isOverdue = timeline.rush_deadline_passed;
  const hoursRemaining = timeline.rush_hours_remaining || 0;
  
  return (
    <div className={`rounded-lg p-3 ${isOverdue ? 'bg-red-500/20 border border-red-500/50' : 'bg-orange-500/20 border border-orange-500/50'}`}>
      <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-400' : 'text-orange-400'}`}>
        <Clock size={16} />
        <span className="font-semibold">
          {isOverdue ? 'RUSH DEADLINE OVERDUE' : '48hr RUSH Deadline'}
        </span>
      </div>
      <div className="text-sm mt-1">
        {isOverdue ? (
          <span className="text-red-300">Deadline was: {timeline.rush_deadline_display}</span>
        ) : (
          <>
            <span className={hoursRemaining < 12 ? 'text-red-400 font-bold' : 'text-orange-300'}>
              {hoursRemaining < 1 
                ? `${Math.round(hoursRemaining * 60)} minutes remaining`
                : `${Math.round(hoursRemaining)} hours remaining`
              }
            </span>
            <span className="text-zinc-400 ml-2">• Due: {timeline.rush_deadline_display}</span>
          </>
        )}
      </div>
    </div>
  );
};

// Real Estate Showcase Video with Dynamic Glow Effect
// Real Estate Hero Video - Simple reliable autoplay
const REHeroVideo = ({ videoUrl, isLoading }) => {
  // No video URL - show placeholder
  if (!videoUrl && !isLoading) {
    return (
      <div className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Demo Video Coming Soon</p>
            <p className="text-sm mt-1">Check back shortly</p>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl animate-pulse"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black aspect-video flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
            <span className="text-zinc-400 text-sm">Loading video...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Glow Effect */}
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-60"></div>
      
      {/* Video Container */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black">
        <video
          src={videoUrl}
          className="w-full aspect-video object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="re-hero-video"
        />
      </div>
    </div>
  );
};

// Real Estate Showcase Video with controls (for lower sections)
const REShowcaseVideo = ({ videoUrl, isLoading, showSoundButton = true }) => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasError, setHasError] = useState(false);

  // Show placeholder if no video or error
  if (!videoUrl || hasError) {
    return (
      <div className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl opacity-50"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-zinc-900 aspect-video flex items-center justify-center">
          <div className="text-center text-zinc-500">
            <Video className="w-16 h-16 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">Demo Video Coming Soon</p>
            <p className="text-sm mt-1">Check back shortly</p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading spinner while fetching video URL
  if (isLoading) {
    return (
      <div className="relative">
        <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl animate-pulse"></div>
        <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black aspect-video flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
            <span className="text-zinc-400 text-sm">Loading video...</span>
          </div>
        </div>
      </div>
    );
  }

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      setCurrentTime(video.currentTime);
      setProgress((video.currentTime / video.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVideoError = () => {
    setHasError(true);
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    if (!video) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * video.duration;
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative">
      {/* Glow Effect */}
      <div className={`absolute -inset-4 md:-inset-6 bg-gradient-to-r from-emerald-500/20 via-emerald-400/30 to-emerald-500/20 rounded-3xl blur-2xl transition-opacity ${isPlaying ? 'opacity-30' : 'opacity-100 animate-pulse'}`}></div>
      
      {/* Video Container */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-500/20 ring-1 ring-emerald-500/20 bg-black">
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full aspect-video object-contain bg-black"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onError={handleVideoError}
          data-testid="re-showcase-video"
        />
        
        {/* Controls overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          {/* Progress bar */}
          <div className="w-full h-1.5 bg-zinc-600 rounded-full cursor-pointer mb-3 group" onClick={handleProgressClick}>
            <div className="h-full bg-emerald-500 rounded-full relative" style={{ width: `${progress}%` }}>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition"></div>
            </div>
          </div>
          
          {/* Controls row */}
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} className="p-1.5 hover:bg-white/20 rounded transition">
              {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white fill-white" />}
            </button>
            <button onClick={toggleMute} className="p-1.5 hover:bg-white/20 rounded transition">
              {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
            </button>
            <span className="text-white text-xs font-mono">{formatTime(currentTime)} / {formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example Video Player with auto-play on scroll and mute toggle
const ExampleVideoPlayer = ({ src, id }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    // Intersection Observer to play/pause based on visibility
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            video.play().catch(() => {});
            setIsPlaying(true);
          } else {
            video.pause();
            setIsPlaying(false);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div 
      ref={containerRef}
      className="group relative mx-auto w-full max-w-2xl"
      data-testid={`example-${id}`}
    >
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 via-amber-500/30 to-yellow-500/20 rounded-xl blur-lg opacity-60 group-hover:opacity-100 transition-opacity duration-500"></div>
      <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-500/40 to-amber-500/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      {/* Video container */}
      <div className="relative rounded-xl overflow-hidden bg-black ring-1 ring-yellow-500/20 group-hover:ring-yellow-500/50 transition-all duration-300 shadow-2xl shadow-black/50">
        <video 
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          className="w-full aspect-video"
        />
        
        {/* Sound toggle button */}
        <button
          onClick={toggleMute}
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-black/80 hover:border-yellow-500/50 transition-all duration-200"
          data-testid={`sound-toggle-${id}`}
          title={isMuted ? "Click for sound" : "Mute"}
        >
          {isMuted ? (
            <VolumeX className="w-5 h-5 text-white/80" />
          ) : (
            <Volume2 className="w-5 h-5 text-yellow-400" />
          )}
        </button>
      </div>
    </div>
  );
};

// Video Book Interactive Visual Component
const VideoBookInteractive = () => {
  const [isOpened, setIsOpened] = useState(false);
  const [videoBookData, setVideoBookData] = useState(null);
  
  // Swapped: First image is now the opened book, second is closed
  const closedBookImage = `${process.env.REACT_APP_BACKEND_URL}/api/storage/images/videobook-closed.png`;
  const openedBookImage = `${process.env.REACT_APP_BACKEND_URL}/api/storage/images/videobook-opened.png`;
  
  // Fetch video book data
  useEffect(() => {
    const fetchVideoBook = async () => {
      try {
        const res = await axios.get(`${API}/videobook`);
        if (res.data?.video_url) {
          setVideoBookData(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch video book:", err);
      }
    };
    fetchVideoBook();
  }, []);
  
  // If video is available, show video instead of static images
  if (videoBookData?.video_url) {
    return (
      <div className="relative max-w-md mx-auto mb-8">
        <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-yellow-500/10 border border-yellow-500/20">
          <video 
            src={`${BACKEND_URL}${videoBookData.video_url}`}
            controls
            loop
            muted
            playsInline
            className="w-full h-auto"
            poster={closedBookImage}
            data-testid="videobook-video"
          />
        </div>
      </div>
    );
  }
  
  // Fallback to static images with click interaction
  return (
    <div className="relative max-w-md mx-auto mb-8">
      <div 
        className="relative cursor-pointer group"
        onClick={() => setIsOpened(!isOpened)}
        data-testid="video-book-interactive"
      >
        {/* Image Container */}
        <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-yellow-500/10 border border-yellow-500/20">
          <img 
            src={isOpened ? openedBookImage : closedBookImage}
            alt={isOpened ? "Video book opened showing HD screen" : "Closed memorial video book"}
            className="w-full h-auto transition-opacity duration-500"
          />
          
          {/* Overlay with prompt - only shown when closed */}
          {!isOpened && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-6">
              <div className="flex items-center gap-2 text-white">
                <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse" />
                <span className="text-sm font-medium">Click to open video book</span>
              </div>
            </div>
          )}
          
          {/* Subtle glow effect */}
          <div className={`absolute inset-0 rounded-2xl transition-all duration-500 pointer-events-none ${isOpened ? 'ring-2 ring-yellow-500/30 shadow-lg shadow-yellow-500/20' : ''}`}></div>
        </div>
        
        {/* Caption when opened */}
        {isOpened && (
          <p className="text-sm text-zinc-500 mt-4 animate-fade-in">
            Click again to close
          </p>
        )}
      </div>
    </div>
  );
};

// Video Book Price Reveal Component
const VideoBookPriceReveal = () => {
  const [isRevealed, setIsRevealed] = useState(false);
  
  return (
    <div className="text-center">
      {!isRevealed ? (
        <button
          onClick={() => setIsRevealed(true)}
          className="text-sm text-zinc-400 hover:text-yellow-500 transition-colors duration-200 underline underline-offset-4 decoration-zinc-600 hover:decoration-yellow-500/50"
          data-testid="view-upgrade-details-btn"
        >
          View upgrade details
        </button>
      ) : (
        <div 
          className="animate-fade-in"
          data-testid="upgrade-details-revealed"
        >
          <p className="text-zinc-300 mb-1">
            <span className="text-yellow-500 font-semibold">$500</span> one-time premium add-on
          </p>
          <p className="text-sm text-zinc-500 italic">
            Available during checkout.
          </p>
        </div>
      )}
    </div>
  );
};

// ============== SELECTOR PAGE (UMBRELLA LANDING) ==============
const SelectorPage = () => {
  const navigate = useNavigate();
  const [hoveredOption, setHoveredOption] = useState(null);
  
  return (
    <div className="min-h-screen pt-20 pb-8 px-4 sm:px-6 relative overflow-hidden" data-testid="selector-page">
      {/* Slick Background */}
      <div className="absolute inset-0 bg-zinc-950">
        {/* Gradient mesh */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-black"></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        
        {/* Floating orbs - smaller on mobile */}
        <div className="absolute top-1/4 left-1/4 w-48 md:w-96 h-48 md:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-float-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-40 md:w-80 h-40 md:h-80 bg-emerald-500/5 rounded-full blur-3xl animate-float-slow-reverse"></div>
        
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.015]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'}}></div>
      </div>
      
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Hero Section */}
        <div className="text-center mb-6 md:mb-10">
          {/* Animated icon */}
          <div className="flex justify-center mb-3 md:mb-4">
            <div className="relative">
              <div className="w-14 h-14 md:w-20 md:h-20 bg-gradient-to-br from-yellow-500/10 to-emerald-500/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/5">
                <Sparkles className="w-7 h-7 md:w-10 md:h-10 text-white/80 animate-flicker" />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-r from-yellow-500/10 to-emerald-500/10 rounded-full blur-xl animate-flicker"></div>
            </div>
          </div>
          
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold font-serif mb-2 md:mb-4 leading-tight">
            What Are You{' '}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-yellow-400 via-emerald-400 to-yellow-400 bg-[length:200%_auto] animate-gradient-x bg-clip-text text-transparent">
                Creating
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-emerald-400 blur-xl opacity-30 animate-flicker"></span>
            </span>
            {' '}Today?
          </h1>
          <p className="text-sm md:text-lg text-zinc-400 max-w-xl mx-auto mb-1">
            We turn your photos into professional cinematic videos
          </p>
          <p className="text-xs md:text-sm text-zinc-500">
            Choose your video type ↓
          </p>
        </div>
        
        {/* Option Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto mb-6 md:mb-10">
          
          {/* Memorial Videos Card */}
          <div 
            onClick={() => navigate('/memorial')}
            onMouseEnter={() => setHoveredOption('memorial')}
            onMouseLeave={() => setHoveredOption(null)}
            className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all duration-500 ${
              hoveredOption === 'memorial' ? 'scale-[1.02] shadow-2xl shadow-yellow-500/20' : 
              hoveredOption === 'real-estate' ? 'opacity-60 scale-[0.98]' : ''
            }`}
            data-testid="select-memorial"
          >
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-900 to-yellow-950/30"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Border */}
            <div className="absolute inset-0 rounded-2xl border-2 border-zinc-800 group-hover:border-yellow-500/50 transition-colors duration-300"></div>
            
            {/* Corner accent */}
            <div className="absolute -top-16 -right-16 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative p-5 md:p-8">
              {/* Icon & Badge */}
              <div className="flex items-start justify-between mb-3 md:mb-5">
                <div className="w-14 h-14 md:w-18 md:h-18 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-yellow-500/30">
                  <Heart className="w-7 h-7 md:w-9 md:h-9 text-black" fill="currentColor" />
                </div>
                <span className="text-[10px] md:text-xs bg-yellow-500/20 text-yellow-400 px-2 md:px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
              
              {/* Content */}
              <h2 className="text-xl md:text-2xl font-bold font-serif mb-2 text-white group-hover:text-yellow-400 transition-colors">
                Memorial Video
              </h2>
              <p className="text-xs md:text-sm text-zinc-400 mb-3 md:mb-5 leading-relaxed">
                Honor your loved ones with a beautiful cinematic tribute for funeral services or celebrations of life.
              </p>
              
              {/* Features - Hidden on small mobile */}
              <div className="hidden sm:flex flex-wrap gap-2 mb-4 md:mb-5">
                <span className="text-[10px] md:text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">Photo Animation</span>
                <span className="text-[10px] md:text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">Music</span>
                <span className="text-[10px] md:text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">HD</span>
              </div>
              
              {/* CTA */}
              <div className="flex items-center justify-between">
                <span className="text-xs md:text-sm text-zinc-500">From <span className="text-yellow-500 font-semibold">$250</span></span>
                <div className="relative">
                  <div className="absolute inset-0 bg-yellow-500/40 rounded-lg md:rounded-xl blur-md animate-flicker"></div>
                  <div className="relative flex items-center gap-1.5 md:gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-sm transition-colors">
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Real Estate Videos Card */}
          <div 
            onClick={() => navigate('/real-estate')}
            onMouseEnter={() => setHoveredOption('real-estate')}
            onMouseLeave={() => setHoveredOption(null)}
            className={`group cursor-pointer relative overflow-hidden rounded-2xl transition-all duration-500 ${
              hoveredOption === 'real-estate' ? 'scale-[1.02] shadow-2xl shadow-emerald-500/20' : 
              hoveredOption === 'memorial' ? 'opacity-60 scale-[0.98]' : ''
            }`}
            data-testid="select-real-estate"
          >
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-900 to-emerald-950/30"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            {/* Border */}
            <div className="absolute inset-0 rounded-2xl border-2 border-zinc-800 group-hover:border-emerald-500/50 transition-colors duration-300"></div>
            
            {/* Corner accent */}
            <div className="absolute -top-16 -right-16 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative p-5 md:p-8">
              {/* Icon & Badge */}
              <div className="flex items-start justify-between mb-3 md:mb-5">
                <div className="w-14 h-14 md:w-18 md:h-18 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <Home className="w-7 h-7 md:w-9 md:h-9 text-black" />
                </div>
                <span className="text-[10px] md:text-xs bg-emerald-500/20 text-emerald-400 px-2 md:px-3 py-1 rounded-full">
                  For Agents
                </span>
              </div>
              
              {/* Content */}
              <h2 className="text-xl md:text-2xl font-bold font-serif mb-2 text-white group-hover:text-emerald-400 transition-colors">
                Real Estate Video
              </h2>
              <p className="text-xs md:text-sm text-zinc-400 mb-3 md:mb-5 leading-relaxed">
                Showcase properties with stunning animated video tours. Stand out on MLS and social media.
              </p>
              
              {/* Features - Hidden on small mobile */}
              <div className="hidden sm:flex flex-wrap gap-2 mb-4 md:mb-5">
                <span className="text-[10px] md:text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">Property Tours</span>
                <span className="text-[10px] md:text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">MLS Ready</span>
                <span className="text-[10px] md:text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded-full">Social</span>
              </div>
              
              {/* CTA */}
              <div className="flex items-center justify-between">
                <span className="text-xs md:text-sm text-zinc-500">From <span className="text-emerald-500 font-semibold">$180</span></span>
                <div className="relative">
                  <div className="absolute inset-0 bg-emerald-500/40 rounded-lg md:rounded-xl blur-md animate-flicker"></div>
                  <div className="relative flex items-center gap-1.5 md:gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-4 md:px-5 py-2 md:py-2.5 rounded-lg md:rounded-xl text-sm transition-colors">
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Section - Compact on mobile */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-zinc-900/80 border border-zinc-800 rounded-full px-3 py-1.5 mb-3">
            <div className="flex -space-x-2">
              <div className="w-5 h-5 md:w-6 md:h-6 bg-yellow-500 rounded-full border-2 border-zinc-900"></div>
              <div className="w-5 h-5 md:w-6 md:h-6 bg-emerald-500 rounded-full border-2 border-zinc-900"></div>
              <div className="w-5 h-5 md:w-6 md:h-6 bg-blue-500 rounded-full border-2 border-zinc-900"></div>
            </div>
            <span className="text-[10px] md:text-xs text-zinc-400">Trusted by <span className="text-white font-semibold">500+</span> clients</span>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-zinc-500 text-xs md:text-sm">
            <div className="flex items-center gap-1.5">
              <Shield className="w-4 h-4 text-yellow-500" />
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span>Fast</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Award className="w-4 h-4 text-yellow-500" />
              <span>Pro Quality</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== REAL ESTATE LANDING PAGE ==============
const RealEstateLandingPage = () => {
  const [packages, setPackages] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Store last visited vertical for context awareness
    localStorage.setItem("lastVertical", "real_estate");
    
    (async () => {
      try {
        const res = await axios.get(`${API}/packages/real-estate`);
        setPackages(res.data);
      } catch (e) {
        console.error("Failed to fetch real estate packages:", e);
      }
    })();
  }, []);

  const steps = [
    { icon: <Camera className="w-8 h-8" />, title: "Upload Photos", desc: "Share your best property photos" },
    { icon: <Upload className="w-8 h-8" />, title: "We Create", desc: "Our editors craft your video" },
    { icon: <Video className="w-8 h-8" />, title: "Review & Download", desc: "Get your professional video" },
    { icon: <Star className="w-8 h-8" />, title: "Close More Deals", desc: "Impress clients & buyers" },
  ];

  const features = [
    { icon: <Award />, title: "Professional Quality", desc: "Cinematic editing that sells" },
    { icon: <Zap />, title: "Fast Turnaround", desc: "Standard 5-7 days, rush available" },
    { icon: <Shield />, title: "MLS Ready", desc: "Perfect dimensions for listings" },
    { icon: <Star />, title: "One Free Revision", desc: "We'll make it perfect for you" },
  ];

  return (
    <div className="min-h-screen" data-testid="real-estate-landing-page">
      {/* Hero Section */}
      <section className="relative pt-24 pb-8 md:pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            <span>Real Estate Video Services</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold font-serif mb-6 animate-fade-in">
            Sell Properties <span className="text-emerald-500">Faster</span>
          </h1>
          <p className="text-xl md:text-2xl text-zinc-400 mb-8 max-w-3xl mx-auto">
            Transform your property photos into stunning video tours that captivate buyers and close deals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate("/real-estate/checkout")} 
              className="btn-primary-emerald text-lg animate-pulse-glow-emerald px-8 py-4"
              data-testid="re-hero-cta"
            >
              Create Property Video <ArrowRight className="inline ml-2" size={20} />
            </button>
            <button 
              onClick={() => document.getElementById('re-pricing').scrollIntoView({ behavior: 'smooth' })}
              className="px-6 py-3 border border-zinc-700 rounded-lg hover:border-zinc-500 transition"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Property Video Showcase - Hero with smooth autoplay */}
      <section className="py-4 md:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <REHeroVideoPlayer />
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-serif text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-500">
                  {step.icon}
                </div>
                <div className="text-2xl font-bold text-emerald-500 mb-2">{i + 1}</div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-zinc-400">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="re-pricing" className="py-20 px-4 bg-zinc-950/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold font-serif text-center mb-4">Pricing That Works for You</h2>
          <p className="text-zinc-400 text-center mb-12 max-w-2xl mx-auto">
            One video. One revision. Delivered to your dashboard in 5–7 business days.
          </p>
          
          {/* Package Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* 10 Photos */}
            <div className="card-dark p-6 text-center border border-zinc-800 hover:border-emerald-500/50 transition">
              <h3 className="text-xl font-bold font-serif mb-2">10 Photos</h3>
              <div className="text-4xl font-bold text-emerald-500 mb-2">$125</div>
              <p className="text-zinc-500 text-xs mb-4">Ideal for condos or smaller listings</p>
              <ul className="text-left text-zinc-400 text-sm space-y-2 mb-4">
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Cinematic property walkthrough</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Optimized for MLS and social media</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> One free revision included</li>
              </ul>
              <p className="text-zinc-600 text-xs mb-4">Delivery: 5–7 business days &middot; Rush available</p>
              <button 
                onClick={() => navigate('/real-estate/checkout?package=10photos')}
                className="w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold transition"
                data-testid="select-10photos-package"
              >
                Select Package
              </button>
            </div>

            {/* 25 Photos - Popular */}
            <div className="beam-border p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-black px-4 py-1 rounded-full text-xs font-bold z-10">MOST POPULAR</div>
              <h3 className="text-xl font-bold font-serif mb-2">25 Photos</h3>
              <div className="text-4xl font-bold text-emerald-500 mb-2">$200</div>
              <p className="text-zinc-500 text-xs mb-4">Ideal for full property storytelling</p>
              <ul className="text-left text-zinc-400 text-sm space-y-2 mb-4">
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Cinematic property walkthrough</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Optimized for MLS and social media</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Designed to increase buyer engagement</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> One free revision included</li>
              </ul>
              <p className="text-zinc-600 text-xs mb-4">Delivery: 5–7 business days &middot; Rush available</p>
              <button 
                onClick={() => navigate('/real-estate/checkout?package=25photos')}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black py-3 rounded-lg font-semibold transition"
                data-testid="select-25photos-package"
              >
                Select Package
              </button>
            </div>

            {/* Custom Photo Count */}
            <div className="card-dark p-6 text-center border border-zinc-800 hover:border-emerald-500/50 transition">
              <h3 className="text-xl font-bold font-serif mb-2">Custom Photo Count</h3>
              <div className="text-4xl font-bold text-emerald-500 mb-2">5+ Photos</div>
              <p className="text-zinc-500 text-xs mb-4">Only pay for what you need</p>
              <ul className="text-left text-zinc-400 text-sm space-y-2 mb-4">
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> 5–9 photos: $18/photo</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> 10–24 photos: $16/photo</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> 25+ photos: $14/photo</li>
              </ul>
              <p className="text-zinc-600 text-xs mb-4">Delivery: 5–7 business days &middot; Rush available</p>
              <button 
                onClick={() => navigate('/real-estate/checkout?package=custom')}
                className="w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold transition"
                data-testid="select-re-custom-package"
              >
                Build Your Package
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video Showcase */}
      <section className="py-12 px-4 bg-zinc-950/30" data-testid="re-demo-video-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold font-serif text-center mb-4">These Were Photos. Now They <span style={{ color: '#10B981', textShadow: '0 0 20px rgba(16,185,129,0.4), 0 0 40px rgba(16,185,129,0.15)' }}>Move</span>.</h2>
          <p className="text-zinc-400 text-center mb-8 max-w-2xl mx-auto">
            Watch your photos turn into videos
          </p>
          <REDemoVideoPlayer />
        </div>
      </section>

      {/* RE FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold font-serif text-center mb-10">Frequently Asked Questions</h2>
          {[
            { q: "How long does delivery take?", a: "Standard delivery is 5\u20137 business days. Need it faster? Add 48-hour or 24-hour rush at checkout." },
            { q: "What format will I receive?", a: "You\u2019ll receive a high-quality video optimized for MLS and social media platforms." },
            { q: "What if I need changes?", a: "Every order includes one free revision to make sure you\u2019re happy with the final result." },
            { q: "How long are the videos?", a: "Video length depends on photo count, but typically ranges from 30\u201390 seconds for optimal engagement." },
            { q: "Where can I use the video?", a: "MLS, Zillow, Instagram, Facebook, YouTube, email marketing, and listing presentations." },
            { q: "Do I need video experience?", a: "No. Just upload your photos. We handle the editing and delivery." },
          ].map((item, i) => (
            <details key={i} className="group border-b border-zinc-800 last:border-0">
              <summary className="flex items-center justify-between py-4 cursor-pointer text-white font-medium hover:text-emerald-400 transition list-none">
                <span>{item.q}</span>
                <ChevronDown size={18} className="text-zinc-500 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="text-zinc-400 text-sm pb-4 pl-1">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Mid Page Video */}
      <section className="py-12 px-4" data-testid="re-mid-video-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold font-serif text-center mb-4">Listen to What These Agents Had to Say</h2>
          <p className="text-zinc-400 text-center mb-8">Real feedback. Real results. Real listings elevated.</p>
          <REMidVideoPlayer />
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-serif text-center mb-12">Why Agents Choose Us</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <div key={i} className="card-dark p-6 text-center">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mx-auto mb-4 text-emerald-500">
                  {f.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-zinc-400 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-zinc-950/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold font-serif mb-6">Ready to Elevate Your Listings?</h2>
          <p className="text-xl text-zinc-400 mb-8">
            Join hundreds of agents who are closing more deals with professional property videos.
          </p>
          <button 
            onClick={() => navigate("/real-estate/checkout")} 
            className="bg-emerald-500 hover:bg-emerald-400 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
            data-testid="re-cta-button"
          >
            Get Started Now <ArrowRight className="inline ml-2" size={20} />
          </button>
        </div>
      </section>
    </div>
  );
};

// ============== PAGES ==============
const LandingPage = () => {
  const { user } = useAuth();
  const [packages, setPackages] = useState(null);
  const [examples, setExamples] = useState([]);
  const [showcaseItems, setShowcaseItems] = useState([]);
  const [currentShowcaseIndex, setCurrentShowcaseIndex] = useState(0);
  const [showcasePlaying, setShowcasePlaying] = useState(false);
  const [showcaseEnded, setShowcaseEnded] = useState(false);
  const [showcaseProgress, setShowcaseProgress] = useState(0);
  const [memorialShowcase2, setMemorialShowcase2] = useState(null);
  const [memorialShowcase2Loading, setMemorialShowcase2Loading] = useState(true);
  // Memorial section visibility settings from admin
  const [memorialSettings, setMemorialSettings] = useState({ enableTopVideo: true, enableCarousel: true, enableBottomVideo: true });
  const showcaseVideoRef = useRef(null);
  const showcaseContainerRef = useRef(null);
  const navigate = useNavigate();

  // Current showcase item
  const currentShowcase = showcaseItems[currentShowcaseIndex] || null;

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/packages`);
      setPackages(res.data);
    } catch (e) {
      console.error("Failed to fetch packages:", e);
    }
  };

  const fetchExamples = async () => {
    try {
      const res = await axios.get(`${API}/examples?vertical=memorial`);
      setExamples(res.data);
    } catch (e) {
      console.error("Failed to fetch examples:", e);
    }
  };

  const fetchShowcase = async () => {
    try {
      const res = await axios.get(`${API}/showcase/all`);
      // Filter only enabled showcases with both photo and video
      const validShowcases = (res.data || []).filter(
        s => s.enabled && s.photo_url && s.video_url
      );
      
      // Verify files actually exist by checking HEAD requests
      const verifiedShowcases = [];
      for (const showcase of validShowcases) {
        try {
          await axios.head(`${BACKEND_URL}${showcase.photo_url}`);
          verifiedShowcases.push(showcase);
        } catch {
          // File doesn't exist, skip this showcase
          console.warn(`Showcase file missing: ${showcase.photo_url}`);
        }
      }
      setShowcaseItems(verifiedShowcases);
    } catch (e) {
      console.error("Failed to fetch showcase:", e);
    }
  };

  useEffect(() => {
    // Store last visited vertical for context awareness
    localStorage.setItem("lastVertical", "memorial");
    
    // Fetch initial data on mount
    (async () => {
      // Fetch memorial section settings
      try {
        const res = await axios.get(`${API}/memorial-settings`);
        setMemorialSettings(res.data || { enableTopVideo: true, enableCarousel: true, enableBottomVideo: true });
      } catch (e) {
        console.error("Failed to fetch memorial settings:", e);
      }
      // Fetch packages
      try {
        const res = await axios.get(`${API}/packages`);
        setPackages(res.data);
      } catch (e) {
        console.error("Failed to fetch packages:", e);
      }
      // Fetch examples
      try {
        const res = await axios.get(`${API}/examples?vertical=memorial`);
        setExamples(res.data);
      } catch (e) {
        console.error("Failed to fetch examples:", e);
      }
      // Fetch showcase
      try {
        const res = await axios.get(`${API}/showcase/all`);
        const validShowcases = (res.data || []).filter(
          s => s.enabled && s.photo_url && s.video_url
        );
        // Verify files actually exist
        const verifiedShowcases = [];
        for (const showcase of validShowcases) {
          try {
            await axios.head(`${BACKEND_URL}${showcase.photo_url}`);
            verifiedShowcases.push(showcase);
          } catch {
            console.warn(`Showcase file missing: ${showcase.photo_url}`);
          }
        }
        setShowcaseItems(verifiedShowcases);
      } catch (e) {
        console.error("Failed to fetch showcase:", e);
      }
      // Fetch memorial showcase2 (bottom video)
      try {
        const res = await axios.get(`${API}/memorial-showcase2/public`);
        setMemorialShowcase2(res.data);
      } catch (e) {
        console.error("Failed to fetch memorial showcase2:", e);
      }
      setMemorialShowcase2Loading(false);
    })();
  }, []);

  // Reset showcase for replay or when switching
  const resetShowcase = () => {
    setShowcasePlaying(false);
    setShowcaseEnded(false);
    setShowcaseProgress(0);
    if (showcaseVideoRef.current) {
      showcaseVideoRef.current.pause();
      showcaseVideoRef.current.currentTime = 0;
    }
  };

  // Navigate to previous showcase
  const goToPreviousShowcase = (e) => {
    e.stopPropagation();
    resetShowcase();
    setCurrentShowcaseIndex(prev => 
      prev === 0 ? showcaseItems.length - 1 : prev - 1
    );
  };

  // Navigate to next showcase
  const goToNextShowcase = (e) => {
    e.stopPropagation();
    resetShowcase();
    setCurrentShowcaseIndex(prev => 
      prev === showcaseItems.length - 1 ? 0 : prev + 1
    );
  };

  // Complete the transition and play video
  const completeTransition = async () => {
    setShowcaseProgress(100);
    setShowcasePlaying(true);
    if (showcaseVideoRef.current) {
      try {
        if (showcaseVideoRef.current.readyState < 3) {
          await new Promise((resolve) => {
            showcaseVideoRef.current.oncanplay = resolve;
          });
        }
        await showcaseVideoRef.current.play();
      } catch (err) {
        console.warn("Video play failed:", err);
      }
    }
  };

  // Simple click handler for BOTH mobile and desktop
  const handleShowcaseClick = (e) => {
    // If already playing, ignore
    if (showcasePlaying) return;
    
    // If ended, reset and return
    if (showcaseEnded) {
      resetShowcase();
      return;
    }
    
    // Trigger the transition animation and play video
    completeTransition();
  };

  const handleShowcaseEnded = () => {
    setShowcasePlaying(false);
    setShowcaseEnded(true);
    setShowcaseProgress(0);
  };

  const steps = [
    { icon: <Camera className="w-8 h-8" />, title: "Select Package", desc: "Choose a package that fits your needs" },
    { icon: <Upload className="w-8 h-8" />, title: "Upload Photos", desc: "Share your cherished memories" },
    { icon: <Video className="w-8 h-8" />, title: "We Create", desc: "Our editors craft your video" },
    { icon: <Heart className="w-8 h-8" />, title: "Receive & Share", desc: "Download and share with family" },
  ];

  const features = [
    { icon: <Award />, title: "Professional Quality", desc: "Cinematic editing with attention to detail" },
    { icon: <Zap />, title: "Fast Turnaround", desc: "Standard 5-7 days, rush available" },
    { icon: <Shield />, title: "Secure & Private", desc: "Your photos are handled with care" },
    { icon: <Star />, title: "One Free Revision", desc: "We'll make it perfect for you" },
  ];

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold font-serif mb-6 animate-fade-in">
            Honor Their <span className="text-yellow-500">Memory</span>
          </h1>
          <p className="text-xl md:text-2xl text-zinc-400 mb-8 max-w-3xl mx-auto">
            Transform cherished photos into beautiful memorial videos that celebrate the life of your loved one.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate("/checkout")} 
              className="btn-primary text-lg animate-pulse-glow"
              data-testid="hero-cta"
            >
              Create Memorial Video <ArrowRight className="inline ml-2" size={20} />
            </button>
            <button 
              onClick={() => document.getElementById('packages').scrollIntoView({ behavior: 'smooth' })}
              className="px-6 py-3 border border-zinc-700 rounded-lg hover:border-zinc-500 transition"
            >
              View Packages
            </button>
          </div>
        </div>
      </section>

      {/* Click-to-Animate Carousel - directly under hero buttons */}
      <MemorialMomentsCarousel isAdmin={!!user && user.role === 'admin'} />

      {/* Memorial Top Video - Only show if enabled */}
      {memorialSettings.enableTopVideo && (
        <section className="py-8 px-4" data-testid="memorial-hero-video-section">
          <div className="max-w-5xl mx-auto">
            <MemorialHeroVideoPlayer />
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-serif text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-yellow-500">
                  {step.icon}
                </div>
                <div className="text-2xl font-bold text-yellow-500 mb-2">{i + 1}</div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-zinc-400">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example Videos - Sleek showcase */}
      {examples.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className={`grid gap-10 ${examples.length === 1 ? 'max-w-3xl mx-auto' : 'md:grid-cols-2'} justify-center`}>
              {examples.map((example) => (
                <ExampleVideoPlayer 
                  key={example.id}
                  src={`${BACKEND_URL}${example.url}`}
                  id={example.id}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* See What We Create - Demo Video */}
      {memorialSettings.enableBottomVideo && (
      <section className="py-12 px-4 bg-zinc-950/30" data-testid="memorial-demo-video-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold font-serif text-center mb-4">See What We Create</h2>
          <p className="text-zinc-400 text-center mb-8 max-w-2xl mx-auto">
            Watch your photos transform into a beautiful cinematic tribute
          </p>
          <MemorialDemoVideoPlayer />
        </div>
      </section>
      )}

      {/* Packages */}
      <section id="packages" className="py-20 px-4 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-serif text-center mb-4">Choose Your Package</h2>
          <p className="text-zinc-400 text-center mb-12">Handcrafted memory films. Not slideshows.</p>
          
          {packages && (
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {Object.entries(packages.packages).map(([id, pkg]) => (
                <div 
                  key={id} 
                  className={`card-dark p-8 relative ${id === 'tier2' ? 'ring-2 ring-yellow-500' : ''} ${id === 'tier3' ? 'ring-2 ring-orange-500' : ''}`}
                  data-testid={`package-${id}`}
                >
                  {id === 'tier1' && (
                    <div className="text-blue-400 text-sm font-semibold mb-2">🔹 TIER 1</div>
                  )}
                  {id === 'tier2' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-yellow-400 text-sm font-semibold">⭐ TIER 2</span>
                      <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">MOST POPULAR</span>
                    </div>
                  )}
                  {id === 'tier3' && (
                    <div className="text-orange-400 text-sm font-semibold mb-2">🔥 TIER 3</div>
                  )}
                  <div className="text-4xl font-bold text-yellow-500 mb-1">${pkg.price.toLocaleString()}</div>
                  {pkg.subtitle && (
                    <div className="text-zinc-400 text-sm mb-4 italic">({pkg.subtitle})</div>
                  )}
                  <ul className="space-y-2 mb-6 text-sm">
                    {pkg.features ? (
                      pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-zinc-300">
                          <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))
                    ) : (
                      <>
                        <li className="flex items-center gap-2 text-zinc-300">
                          <Check size={14} className="text-green-500" /> Up to {pkg.photos} photos
                        </li>
                        <li className="flex items-center gap-2 text-zinc-300">
                          <Check size={14} className="text-green-500" /> {pkg.video_duration} video
                        </li>
                      </>
                    )}
                  </ul>
                  {pkg.tagline && (
                    <p className="text-xs text-zinc-500 italic mb-4">{pkg.tagline}</p>
                  )}
                  <button 
                    onClick={() => navigate(`/checkout?package=${id}`)}
                    className={`w-full py-3 rounded-lg font-semibold transition ${
                      id === 'tier2' ? 'bg-yellow-500 hover:bg-yellow-400 text-black' : 
                      id === 'tier3' ? 'bg-orange-500 hover:bg-orange-400 text-black' :
                      'bg-zinc-800 hover:bg-zinc-700'
                    }`}
                    data-testid={`select-${id}`}
                  >
                    Select Package
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Custom Package */}
          <div className="card-dark p-8 max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold font-serif mb-2">Custom Package</h3>
            <p className="text-zinc-400 mb-4">Need a specific number of photos? Build your own package.</p>
            <div className="text-3xl font-bold text-yellow-500 mb-2">
              Starting at ${packages?.custom_price_per_photo || 25}/photo
            </div>
            <p className="text-zinc-500 text-sm mb-2">Volume discounts available at 38+ and 125+ photos</p>
            <p className="text-zinc-500 text-sm mb-4">Minimum {packages?.custom_min_photos || 5} photos • One free revision</p>
            <button 
              onClick={() => navigate('/checkout?package=custom')}
              className="bg-zinc-800 hover:bg-zinc-700 px-8 py-3 rounded-lg font-semibold transition"
              data-testid="select-custom"
            >
              Build Custom Package
            </button>
          </div>
        </div>
      </section>

      {/* Video Book Introduction - Premium Add-On */}
      <section className="py-12 px-4" data-testid="video-book-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-yellow-500/70 mb-4">
            Optional Premium Add-On
          </p>
          <h2 className="text-4xl font-bold font-serif mb-6">A Memorial You Can Hold</h2>
          <p className="text-lg text-zinc-300 leading-relaxed mb-10 max-w-2xl mx-auto">
            A premium physical upgrade featuring a video book with a built-in HD screen that plays your loved one&apos;s memorial video the moment it&apos;s opened. Designed to be displayed, shared, and preserved for generations.
          </p>
          <VideoBookInteractive />
          <VideoBookPriceReveal />
        </div>
      </section>

      {/* Features - Why Choose Us */}
      <section className="py-20 px-4 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold font-serif text-center mb-12">Why Choose Us</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <div key={i} className="card-dark p-6 text-center">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mx-auto mb-4 text-yellow-500">
                  {f.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-zinc-400 text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold font-serif mb-6">Ready to Create Something Beautiful?</h2>
          <p className="text-xl text-zinc-400 mb-8">
            Let us help you create a lasting tribute to your loved one.
          </p>
          <button 
            onClick={() => navigate("/checkout")} 
            className="btn-primary text-lg"
            data-testid="cta-button"
          >
            Get Started Now <ArrowRight className="inline ml-2" size={20} />
          </button>
        </div>
      </section>
    </div>
  );
};

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Determine if this is a real estate context
  const isRealEstate = redirectTo.includes('real-estate');
  const accentColor = isRealEstate ? 'emerald' : 'yellow';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      // Redirect based on user's vertical
      const uv = result?.user?.vertical;
      if (redirectTo && redirectTo !== "/dashboard") {
        navigate(redirectTo);
      } else if (uv === "real_estate") {
        navigate("/dashboard");
      } else if (uv === "memorial") {
        navigate("/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md">
        <BackButton to={isRealEstate ? "/real-estate" : "/"} label={isRealEstate ? "Back to Real Estate" : "Back to Home"} />
        <div className="card-dark p-8">
          <h1 className="text-3xl font-bold font-serif text-center mb-6">Welcome Back</h1>
          {error && <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-3 rounded-lg mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-${accentColor}-500`}
              required
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-${accentColor}-500`}
                required
                data-testid="login-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white" tabIndex={-1}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button 
            type="submit" 
            disabled={loading} 
            className={`w-full py-3 rounded-lg font-semibold transition ${isRealEstate ? 'bg-emerald-500 hover:bg-emerald-400 text-black' : 'bg-yellow-500 hover:bg-yellow-400 text-black'}`}
            data-testid="login-submit"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/forgot-password" className={`text-${accentColor}-500 hover:text-${accentColor}-400 text-sm`}>Forgot password?</Link>
        </div>
        <div className="mt-4 text-center text-zinc-400">
          Don&apos;t have an account? <Link to={`/signup?redirect=${encodeURIComponent(redirectTo)}`} className={`text-${accentColor}-500 hover:text-${accentColor}-400`}>Sign up</Link>
        </div>
        </div>
      </div>
    </div>
  );
};

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/checkout";
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "customer",
    marketing_opt_in: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSignupPw, setShowSignupPw] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  
  // Determine if this is a real estate context
  const isRealEstate = redirectTo.includes('real-estate');
  const accentColor = isRealEstate ? 'emerald' : 'yellow';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup({ ...formData, vertical: isRealEstate ? "real_estate" : "memorial" });
      // New users go to checkout, not dashboard
      navigate(redirectTo);
    } catch (e) {
      setError(e.response?.data?.detail || "Signup failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="signup-page">
      <div className="w-full max-w-md">
        <BackButton to={isRealEstate ? "/real-estate" : "/"} label={isRealEstate ? "Back to Real Estate" : "Back to Home"} />
        <div className="card-dark p-8">
          <h1 className="text-3xl font-bold font-serif text-center mb-6">Create Account</h1>
          {error && <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-3 rounded-lg mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-${accentColor}-500`}
                required
                data-testid="signup-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-${accentColor}-500`}
                required
                data-testid="signup-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <input
                  type={showSignupPw ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-${accentColor}-500`}
                  required
                  minLength={6}
                  data-testid="signup-password"
                />
                <button type="button" onClick={() => setShowSignupPw(!showSignupPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white" tabIndex={-1}>
                  {showSignupPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="marketing"
              checked={formData.marketing_opt_in}
              onChange={(e) => setFormData({ ...formData, marketing_opt_in: e.target.checked })}
              className={`w-4 h-4 accent-${accentColor}-500`}
              data-testid="signup-marketing"
            />
            <label htmlFor="marketing" className="text-sm text-zinc-400">
              Send me updates and promotional emails
            </label>
          </div>
          <button 
            type="submit" 
            disabled={loading} 
            className={`w-full py-3 rounded-lg font-semibold transition ${isRealEstate ? 'bg-emerald-500 hover:bg-emerald-400 text-black' : 'bg-yellow-500 hover:bg-yellow-400 text-black'}`}
            data-testid="signup-submit"
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>
        <div className="mt-4 text-center text-zinc-400">
          Already have an account? <Link to={`/login?redirect=${encodeURIComponent(redirectTo)}`} className={`text-${accentColor}-500 hover:text-${accentColor}-400`}>Sign in</Link>
        </div>
        </div>
      </div>
    </div>
  );
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/password-reset-request`, { email });
      setSent(true);
    } catch (err) {
      console.error("Password reset request failed:", err);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
      <div className="card-dark p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold font-serif text-center mb-6">Reset Password</h1>
        {sent ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-500" size={32} />
            </div>
            <p className="text-zinc-300">If an account exists with that email, we&apos;ve sent password reset instructions.</p>
            <Link to="/login" className="text-yellow-500 hover:text-yellow-400 mt-4 inline-block">Back to login</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 focus:outline-none focus:border-yellow-500"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState(token ? "ready" : "invalid");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/password-reset`, { token, new_password: password });
      setStatus("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed. The link may have expired.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
      <div className="card-dark p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold font-serif text-center mb-6">Set New Password</h1>
        {status === "invalid" && (
          <div className="text-center">
            <p className="text-red-400 mb-4">Invalid or missing reset token.</p>
            <Link to="/forgot-password" className="text-yellow-500 hover:text-yellow-400">Request a new reset link</Link>
          </div>
        )}
        {status === "done" && (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-500" size={32} />
            </div>
            <p className="text-zinc-300 mb-4">Your password has been reset successfully.</p>
            <Link to="/login" className="text-yellow-500 hover:text-yellow-400 font-semibold">Log In</Link>
          </div>
        )}
        {status === "ready" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <div className="relative">
                <input type={showResetPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-yellow-500" required minLength={6} />
                <button type="button" onClick={() => setShowResetPw(!showResetPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white" tabIndex={-1}>
                  {showResetPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Confirm Password</label>
              <div className="relative">
                <input type={showResetPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:border-yellow-500" required minLength={6} />
                <button type="button" onClick={() => setShowResetPw(!showResetPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white" tabIndex={-1}>
                  {showResetPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const sessionId = searchParams.get("session_id");
  const orderId = searchParams.get("order_id");

  useEffect(() => {
    if (!sessionId) return;
    
    let cancelled = false;
    
    const pollPaymentStatus = async (attempt) => {
      if (cancelled || attempt >= 10) {
        if (!cancelled && attempt >= 10) setStatus("timeout");
        return;
      }

      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        
        if (res.data.payment_status === "paid") {
          setStatus("success");
          // Fetch order details
          try {
            const orderRes = await axios.get(`${API}/orders/by-session/${sessionId}`);
            if (!cancelled) setOrder(orderRes.data);
          } catch (err) {
            console.error("Failed to fetch order details:", err);
          }
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else {
          setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
        }
      } catch (err) {
        if (!cancelled) setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
      }
    };
    
    pollPaymentStatus(0);
    
    return () => { cancelled = true; };
  }, [sessionId]);

  if (status === "checking") {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="payment-checking">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold mb-2">Verifying Payment...</h1>
          <p className="text-zinc-400">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="payment-success">
        <div className="card-dark p-8 max-w-lg text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="text-green-500" size={40} />
          </div>
          <h1 className="text-3xl font-bold font-serif mb-4">Payment Successful!</h1>
          <p className="text-zinc-400 mb-6">
            Thank you for your order. You can now upload your photos.
          </p>
          {order && (
            <div className="bg-zinc-800 rounded-lg p-4 mb-6 text-left">
              <div className="text-sm text-zinc-400 mb-1">Order Number</div>
              <div className="font-bold text-yellow-500">{order.order_number}</div>
            </div>
          )}
          <button
            onClick={() => navigate(order ? `/orders/${order.id}` : "/dashboard")}
            className="btn-primary"
            data-testid="go-to-upload"
          >
            Go to Upload Portal <ArrowRight className="inline ml-2" size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
      <div className="card-dark p-8 max-w-lg text-center">
        <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="text-red-500" size={40} />
        </div>
        <h1 className="text-3xl font-bold font-serif mb-4">Payment Issue</h1>
        <p className="text-zinc-400 mb-6">
          {status === "expired" 
            ? "Your payment session has expired. Please try again."
            : "We couldn't verify your payment. Please check your email for confirmation or contact support."}
        </p>
        <button onClick={() => navigate("/checkout")} className="btn-primary">
          Try Again
        </button>
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [supportEmail, setSupportEmail] = useState("izzopro@izzopro.com");
  const location = useLocation();
  
  // Detect if user came from real estate pages
  const isRealEstate = location.state?.from === "real_estate" || 
    document.referrer.includes("/real-estate") ||
    localStorage.getItem("lastVertical") === "real_estate";

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/public`);
        if (res.data.support_email) {
          setSupportEmail(res.data.support_email);
        }
      } catch (e) {
        // Use default
      }
    };
    fetchSettings();
  }, []);

  // Memorial FAQs
  const memorialFaqs = [
    { q: "How long does it take to receive my memorial video?", a: "Standard delivery is 5-7 business days. Rush delivery (48 hours) is available for an additional $299." },
    { q: "What file formats do you accept for photos?", a: "We accept JPG, PNG, and HEIC formats. For best results, upload high-resolution images." },
    { q: "Can I include video clips in addition to photos?", a: "Currently, we focus on photo-based memorial videos. Video clip integration may be available in the future." },
    { q: "How long will my video be?", a: "Video length depends on your package: Tier 1 (~2 min), Tier 2 (~5 min), Tier 3 (~10 min). Custom packages vary based on photo count." },
    { q: "What music options are available?", a: "We use royalty-free music from our curated library. For a specific song, add the Song Request option (+$50)." },
    { q: "Can I request changes to my video?", a: "Yes! Every order includes one free revision. Additional revisions are available for $50 each within 7 days of video delivery." },
    { q: "How long do I have to download my video?", a: "Completed videos are available for download for 14 days. After that, they are automatically deleted for privacy." },
    { q: "Is my data secure?", a: "Yes, we take privacy seriously. All photos are stored securely and deleted after 14 days of order completion." },
    { q: "What payment methods do you accept?", a: "We accept all major credit cards through our secure Stripe payment system." },
    { q: "Can I get a refund?", a: "Refunds are available within 7 days if you haven't confirmed your photo upload. After upload confirmation, we cannot offer refunds as work begins immediately." },
  ];

  // Real Estate FAQs
  const realEstateFaqs = [
    { q: "How long does it take to receive my property video?", a: "Standard delivery is 5-7 business days. Rush options available: 48-hour delivery (+$199) or 24-hour delivery (+$299)." },
    { q: "What photo formats do you accept?", a: "We accept JPG, PNG, and HEIC formats. For best results, upload high-resolution property photos (minimum 1920x1080)." },
    { q: "How many photos should I upload?", a: "This depends on your package: 10 Photos ($125) or 25 Photos ($200). You can also build a custom package starting from 5 photos." },
    { q: "Will my video be MLS-ready?", a: "Yes! All our property videos are delivered in MLS-compliant formats and dimensions, ready for immediate upload to listing platforms." },
    { q: "How long will my property video be?", a: "Video length scales with your photo count. More photos means a longer, more detailed property showcase." },
    { q: "What music is included?", a: "We use professional royalty-free music that complements real estate presentations. The music is licensed for commercial use on MLS and social media." },
    { q: "Can I request changes to my video?", a: "Yes! All packages include 1 free revision. Additional revisions are $50 each." },
    { q: "Can I use the video on social media?", a: "Absolutely! You have full rights to use your property video on any platform: MLS, Zillow, Redfin, Instagram, Facebook, YouTube, and more." },
    { q: "How long do I have to download my video?", a: "Completed videos are available for download for 14 days. We recommend downloading immediately and saving to your own storage." },
    { q: "What payment methods do you accept?", a: "We accept all major credit cards through our secure Stripe payment system." },
  ];

  const faqs = isRealEstate ? realEstateFaqs : memorialFaqs;
  const backUrl = isRealEstate ? "/real-estate" : "/";
  const backLabel = isRealEstate ? "Back to Real Estate" : "Back to Home";
  const accentColor = isRealEstate ? "text-emerald-500" : "text-yellow-500";

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="faq-page">
      <div className="max-w-3xl mx-auto">
        <BackButton to={backUrl} label={backLabel} />
        <h1 className="text-4xl font-bold font-serif text-center mb-2">Frequently Asked Questions</h1>
        {isRealEstate && (
          <p className="text-center text-emerald-500 mb-8">Real Estate Property Videos</p>
        )}
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="card-dark overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-6 py-4 flex justify-between items-center text-left"
                data-testid={`faq-${i}`}
              >
                <span className="font-medium">{faq.q}</span>
                <ChevronDown className={`transform transition ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-6 pb-4 text-zinc-400">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
        
        {/* Contact Section */}
        <div className="mt-8 card-dark p-6 text-center">
          <h2 className="font-semibold mb-2">Still have questions?</h2>
          <p className="text-zinc-400">
            Contact us at{" "}
            <a href={`mailto:${supportEmail}`} className={`${accentColor} hover:underline`}>
              {supportEmail}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Secure Download Page - Handles photo downloads from email links
const SecureDownloadPage = () => {
  const { code } = useParams();
  const [status, setStatus] = useState("loading"); // loading, ready, downloading, complete, error
  const [orderInfo, setOrderInfo] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const verifyCode = async () => {
      if (!code) {
        setError("Invalid download link");
        setStatus("error");
        return;
      }

      try {
        const res = await axios.get(`${API}/s/verify/${code}`);
        if (res.data.valid && res.data.action === "download_photos") {
          setOrderInfo(res.data);
          setStatus("ready");
        } else if (!res.data.valid) {
          setError(res.data.is_expired ? "This download link has expired" : "Invalid download link");
          setStatus("error");
        } else {
          setError("This link is not for downloading photos");
          setStatus("error");
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired link");
        setStatus("error");
      }
    };

    verifyCode();
  }, [code]);

  const handleDownload = async () => {
    setStatus("downloading");
    setProgress(0);

    try {
      const response = await axios.get(`${API}/s/download/${code}`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || progressEvent.loaded));
          setProgress(percentCompleted);
        }
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `photos_${code}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setStatus("complete");
    } catch (err) {
      setError(err.response?.data?.detail || "Download failed. Please try again.");
      setStatus("error");
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="card-dark p-8 max-w-md text-center">
          <RefreshCw className="w-12 h-12 text-yellow-500 mx-auto mb-4 animate-spin" />
          <p className="text-zinc-400">Verifying download link...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="card-dark p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Download Error</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <p className="text-sm text-zinc-500">
            If you need assistance, please contact support.
          </p>
        </div>
      </div>
    );
  }

  if (status === "complete") {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="card-dark p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="text-green-500" size={40} />
          </div>
          <h1 className="text-2xl font-bold mb-4">Download Complete!</h1>
          <p className="text-zinc-400 mb-4">
            The photos ZIP file has been downloaded to your device.
          </p>
          <button
            onClick={handleDownload}
            className="text-yellow-500 hover:text-yellow-400 text-sm"
          >
            Download Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
      <div className="card-dark p-8 max-w-md text-center">
        {status === "downloading" ? (
          <>
            <Download className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-bounce" />
            <h1 className="text-2xl font-bold mb-4">Downloading Photos...</h1>
            <div className="w-full bg-zinc-700 rounded-full h-3 mb-4">
              <div 
                className="bg-yellow-500 h-3 rounded-full transition-all duration-300" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-zinc-400">{progress}% complete</p>
            <p className="text-xs text-zinc-500 mt-2">Please wait, this may take a moment for large files...</p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileDown className="text-yellow-500" size={40} />
            </div>
            <h1 className="text-2xl font-bold mb-2">Download Customer Photos</h1>
            <p className="text-zinc-400 mb-6">
              Click the button below to download all customer photos as a ZIP file.
            </p>
            
            <button
              onClick={handleDownload}
              className="btn-primary w-full flex items-center justify-center gap-2 text-lg py-4"
              data-testid="download-photos-btn"
            >
              <Download size={20} />
              Download Photos (ZIP)
            </button>
            
            <p className="text-xs text-zinc-500 mt-4">
              Access code: <span className="font-mono text-yellow-500">{code?.toUpperCase()}</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Editor Upload Page (Secure Link)
const EditorUploadPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const code = searchParams.get("code");  // New short code support
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch order info with token or code
    const fetchOrderInfo = async () => {
      if (!token && !code) {
        setError("Invalid or missing access code");
        setLoading(false);
        return;
      }
      
      try {
        let res;
        if (code) {
          // Use new short code endpoint
          res = await axios.get(`${API}/s/upload/${code}`);
        } else {
          // Fall back to old token endpoint
          res = await axios.get(`${API}/secure/order-info?token=${token}`);
        }
        setOrderInfo(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired link");
      }
      setLoading(false);
    };
    
    fetchOrderInfo();
  }, [token, code]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate MP4
    if (!file.name.toLowerCase().endsWith('.mp4')) {
      setError("Only MP4 files are allowed");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      // Use the order_id from orderInfo for upload (works with both code and token)
      const orderId = orderInfo?.order_id;
      if (orderId) {
        // Direct upload using order_id (requires re-verification but more reliable)
        await axios.post(`${API}/orders/${orderId}/upload-video-secure?code=${code || ''}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
      } else if (token) {
        // Fall back to token-based upload
        await axios.post(`${API}/secure/upload-video?token=${token}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
      }
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error && !orderInfo) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="card-dark p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Link Error</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
        <div className="card-dark p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="text-green-500" size={40} />
          </div>
          <h1 className="text-2xl font-bold mb-4">Upload Successful!</h1>
          <p className="text-zinc-400 mb-4">
            The video has been uploaded and the customer has been notified.
          </p>
          <p className="text-sm text-zinc-500">
            Order {orderInfo?.order_number} is now complete.
          </p>
        </div>
      </div>
    );
  }

  const timeline = orderInfo?.timeline || {};
  const isRush = orderInfo?.is_rush;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="editor-upload-page">
      <div className="max-w-2xl mx-auto">
        <div className="card-dark p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold font-serif mb-2">Editor Upload Portal</h1>
            <p className="text-zinc-400">Upload the final memorial video</p>
          </div>

          {/* Order Info */}
          <div className="bg-zinc-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-lg">{orderInfo?.order_number}</span>
              <div className="flex items-center gap-2">
                {isRush && <RushBadge />}
                {orderInfo?.video_book && <VideoBookBadge />}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-zinc-400">Customer:</span> {orderInfo?.customer_name}</div>
              <div><span className="text-zinc-400">Package:</span> {orderInfo?.package_name}</div>
              <div><span className="text-zinc-400">Photos:</span> {orderInfo?.photo_count}</div>
              <div><span className="text-zinc-400">Status:</span> {orderInfo?.status}</div>
            </div>
            {/* Video Book Shipping Info for Editor */}
            {orderInfo?.video_book && orderInfo?.shipping_address && (
              <div className="mt-3 pt-3 border-t border-zinc-700">
                <div className="text-xs text-yellow-500 font-semibold mb-1 flex items-center gap-1">
                  <Truck size={12} /> VIDEO BOOK - Ship To:
                </div>
                <div className="text-xs text-zinc-400">
                  {orderInfo.shipping_address.street}, {orderInfo.shipping_address.city}, {orderInfo.shipping_address.state} {orderInfo.shipping_address.zip}
                </div>
              </div>
            )}
          </div>

          {/* Rush Deadline */}
          {isRush && timeline.rush_deadline_display && (
            <div className={`rounded-lg p-4 mb-6 ${timeline.rush_deadline_passed ? 'bg-red-500/20 border border-red-500/50' : 'bg-orange-500/20 border border-orange-500/50'}`}>
              <div className={`flex items-center gap-2 font-semibold ${timeline.rush_deadline_passed ? 'text-red-400' : 'text-orange-400'}`}>
                <Zap size={18} />
                {timeline.rush_deadline_passed ? 'RUSH DEADLINE OVERDUE' : '48hr RUSH Deadline'}
              </div>
              <div className="text-sm mt-1">
                {timeline.rush_deadline_passed ? (
                  <span className="text-red-300">Was due: {timeline.rush_deadline_display}</span>
                ) : (
                  <>
                    <span className="text-orange-300 font-bold">{Math.round(timeline.rush_hours_remaining || 0)} hours remaining</span>
                    <span className="text-zinc-400 ml-2">• Due: {timeline.rush_deadline_display}</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Upload Status Check */}
          {orderInfo?.upload_locked ? (
            <div className="bg-zinc-800 rounded-lg p-6 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-semibold mb-2">Video Already Uploaded</h2>
              <p className="text-zinc-400">This order has been completed. Upload is locked.</p>
            </div>
          ) : (
            <>
              {/* Upload Section */}
              <div className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center hover:border-yellow-500/50 transition">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".mp4,video/mp4"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="video-file-input"
                />
                <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Upload Final Video</h3>
                <p className="text-zinc-400 text-sm mb-4">MP4 format only • One upload allowed</p>
                
                {uploading ? (
                  <div className="w-full max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-zinc-400">Uploading...</span>
                      <span className="text-sm text-yellow-500 font-semibold">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-3 overflow-hidden">
                      <div 
                        className="bg-yellow-500 h-full rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">Please wait, do not close this page...</p>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-primary"
                    data-testid="select-video-btn"
                  >
                    Select MP4 File
                  </button>
                )}
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 p-3 rounded-lg mt-4">
                  {error}
                </div>
              )}

              <p className="text-xs text-zinc-500 text-center mt-4">
                After upload, the customer will be notified automatically and can download their video.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== THANK YOU PAGE ==============
const ThankYouPage = () => {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [supportEmail, setSupportEmail] = useState("izzopro@izzopro.com");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch order
        const orderRes = await axios.get(`${API}/orders/${orderId}`);
        setOrder(orderRes.data);
        
        // Fetch referral code
        if (user) {
          const refRes = await axios.get(`${API}/referral/my-code`);
          setReferralCode(refRes.data);
        }
        
        // Fetch support email
        const settingsRes = await axios.get(`${API}/settings/public`);
        if (settingsRes.data.support_email) {
          setSupportEmail(settingsRes.data.support_email);
        }
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, [orderId, user]);

  const copyReferralLink = () => {
    if (referralCode?.code) {
      navigator.clipboard.writeText(`https://still2move.com/checkout?ref=${referralCode.code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Determine vertical-specific styling and content
  const isRealEstate = order?.vertical === "real_estate";
  const accentColor = isRealEstate ? "emerald" : "yellow";
  const gradientFrom = isRealEstate ? "from-emerald-400" : "from-yellow-400";
  const gradientTo = isRealEstate ? "to-emerald-600" : "to-yellow-600";
  const IconComponent = isRealEstate ? Home : Heart;
  const thankYouMessage = isRealEstate 
    ? "For trusting us with your property showcase"
    : "For trusting us to honor your loved one's memory";
  const heartfeltMessage = isRealEstate
    ? "We hope this property video helps showcase the home beautifully and attracts the right buyers."
    : "We hope this memorial video brings comfort and helps preserve the beautiful memories of your loved one.";
  const referralMessage = isRealEstate
    ? "Know another agent who could benefit from property videos?"
    : "Know someone who could benefit from a memorial video?";

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="thank-you-page">
      <div className="max-w-2xl mx-auto text-center">
        {/* Emotional Header */}
        <div className="mb-8">
          <div className={`w-24 h-24 bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-${accentColor}-500/20`}>
            <IconComponent className="w-12 h-12 text-black" fill="currentColor" />
          </div>
          <h1 className={`text-4xl md:text-5xl font-bold font-serif mb-4 bg-gradient-to-r ${gradientFrom} ${isRealEstate ? 'to-emerald-200' : 'to-yellow-200'} bg-clip-text text-transparent`}>
            Thank You
          </h1>
          <p className="text-xl text-zinc-300">
            {thankYouMessage}
          </p>
        </div>

        {/* Heartfelt Message */}
        <div className={`card-dark p-8 mb-8 border border-${accentColor}-500/20`}>
          <p className="text-lg text-zinc-300 leading-relaxed mb-4">
            {heartfeltMessage}
          </p>
          <p className="text-zinc-400">
            Thank you for letting us be part of this {isRealEstate ? 'important listing' : 'meaningful journey'} with you.
          </p>
          {order && (
            <div className="mt-6 pt-6 border-t border-zinc-700">
              <p className="text-sm text-zinc-500">Order: {order.order_number}</p>
            </div>
          )}
        </div>

        {/* Referral Section */}
        {user && referralCode && (
          <div className="card-dark p-6 mb-8 bg-gradient-to-br from-zinc-900 to-zinc-800 border border-zinc-700">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className={`text-${accentColor}-500`} size={24} />
              <h2 className="text-xl font-semibold">Share {isRealEstate ? 'With Colleagues' : 'the Gift'}</h2>
            </div>
            <p className="text-zinc-400 mb-4">
              {referralMessage}<br />
              Share your code and they&apos;ll get a special welcome, plus you&apos;ll receive <span className={`text-${accentColor}-500 font-semibold`}>${referralCode.discount_amount} off</span> your next order!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className={`bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-3 font-mono text-lg text-${accentColor}-500`}>
                {referralCode.code}
              </div>
              <button
                onClick={copyReferralLink}
                className={`bg-${accentColor}-500 hover:bg-${accentColor}-600 text-black font-semibold px-4 py-3 rounded-lg transition`}
                data-testid="copy-referral-btn"
              >
                {copied ? "Copied!" : "Copy Link"}
              </button>
            </div>
            
            <p className="text-xs text-zinc-500">
              Your referral link: still2move.com/checkout?ref={referralCode.code}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="btn-primary"
            data-testid="back-to-dashboard-btn"
          >
            View My Orders
          </button>
          <button
            onClick={() => navigate("/")}
            className="bg-zinc-800 hover:bg-zinc-700 px-6 py-3 rounded-lg font-semibold transition"
          >
            Back to Home
          </button>
        </div>

        {/* Support Note */}
        <p className="text-sm text-zinc-500">
          Questions? Contact us at{" "}
          <a href={`mailto:${supportEmail}`} className="text-yellow-500 hover:underline">
            {supportEmail}
          </a>
        </p>
      </div>
    </div>
  );
};

const TermsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vertical = searchParams.get("vertical") || "memorial";
  const isRealEstate = vertical === "real_estate";
  const accentColor = isRealEstate ? "emerald" : "yellow";
  
  const serviceName = isRealEstate ? "real estate property video" : "memorial video";
  const serviceDescription = isRealEstate 
    ? "Still 2 Move provides professional real estate property video creation services using customer-provided property photographs."
    : "Still 2 Move provides memorial video creation services using customer-provided photographs and licensed music.";
  const contentOwnership = isRealEstate
    ? "You retain ownership of all property photos you upload. By uploading, you grant Still 2 Move a limited license to use the photos solely for creating your property video."
    : "You retain ownership of all photos you upload. By uploading, you grant Still 2 Move a limited license to use the photos solely for creating your memorial video.";
  
  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className={`inline-flex items-center gap-2 text-zinc-400 hover:text-${accentColor}-500 transition-colors mb-6 group`}
          data-testid="terms-back-button"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </button>
        <div className="prose prose-invert">
          <h1 className="text-4xl font-bold font-serif mb-8">
            Terms of Service
            {isRealEstate && <span className="text-emerald-500 text-lg ml-3">(Real Estate)</span>}
            {!isRealEstate && <span className="text-yellow-500 text-lg ml-3">(Memorial)</span>}
          </h1>
          <div className="card-dark p-8 text-zinc-300">
            <h2 className="text-xl font-semibold mt-0">1. Acceptance of Terms</h2>
            <p>By accessing and using Still 2 Move {serviceName} services, you agree to be bound by these Terms of Service.</p>

            <h2 className="text-xl font-semibold">2. Service Description</h2>
            <p>{serviceDescription}</p>

            <h2 className="text-xl font-semibold">3. Payment Terms</h2>
            <p>All payments are processed through Stripe. Prices are in USD. Payment is required before photo upload and video creation begins.</p>

            <h2 className="text-xl font-semibold">4. Revisions Policy</h2>
            <p>Each order includes one (1) free revision. Additional revisions may be subject to additional fees.</p>

            <h2 className="text-xl font-semibold">5. Content Ownership</h2>
            <p>{contentOwnership}</p>

            <h2 className="text-xl font-semibold">6. Data Retention</h2>
            <p>Uploaded photos and completed videos are automatically deleted 14 days after order completion.</p>

            <h2 className="text-xl font-semibold">7. Refund Policy</h2>
            <p>Refunds are available within 7 days if photo upload has not been confirmed. After confirmation, no refunds are available as production begins immediately.</p>

            <h2 className="text-xl font-semibold">8. Limitation of Liability</h2>
            <p>Still 2 Move is not liable for any indirect, incidental, or consequential damages arising from use of our services.</p>

            <p className="text-zinc-500 mt-8">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivacyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vertical = searchParams.get("vertical") || "memorial";
  const isRealEstate = vertical === "real_estate";
  const accentColor = isRealEstate ? "emerald" : "yellow";
  
  const videoType = isRealEstate ? "property video" : "memorial video";
  
  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className={`inline-flex items-center gap-2 text-zinc-400 hover:text-${accentColor}-500 transition-colors mb-6 group`}
          data-testid="privacy-back-button"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </button>
        <div className="prose prose-invert">
          <h1 className="text-4xl font-bold font-serif mb-8">
            Privacy Policy
            {isRealEstate && <span className="text-emerald-500 text-lg ml-3">(Real Estate)</span>}
            {!isRealEstate && <span className="text-yellow-500 text-lg ml-3">(Memorial)</span>}
          </h1>
          <div className="card-dark p-8 text-zinc-300">
            <h2 className="text-xl font-semibold mt-0">Information We Collect</h2>
            <p>We collect information you provide directly: name, email, phone number, photos, and payment information (processed by Stripe).</p>

            <h2 className="text-xl font-semibold">How We Use Your Information</h2>
            <ul>
              <li>To create your {videoType}</li>
              <li>To communicate about your order</li>
              <li>To process payments</li>
              <li>To send marketing communications (only if opted in)</li>
            </ul>

            <h2 className="text-xl font-semibold">Data Security</h2>
            <p>We implement industry-standard security measures to protect your data. Photos are stored securely and access is restricted to authorized personnel only.</p>

            <h2 className="text-xl font-semibold">Data Retention</h2>
            <p>Photos and videos are automatically deleted 14 days after order completion. Account information is retained until you request deletion.</p>

            <h2 className="text-xl font-semibold">Third-Party Services</h2>
            <p>We use Stripe for payment processing. Your payment information is handled directly by Stripe according to their privacy policy.</p>

            <h2 className="text-xl font-semibold">Your Rights</h2>
            <p>You have the right to access, correct, or delete your personal information. Contact us at izzopro@izzopro.com for any privacy-related requests.</p>

            <h2 className="text-xl font-semibold">Marketing Communications</h2>
            <p>Marketing emails are only sent to users who opt in during registration. You can unsubscribe at any time.</p>

            <h2 className="text-xl font-semibold">Contact Us</h2>
            <p>For privacy concerns, contact: izzopro@izzopro.com</p>

            <p className="text-zinc-500 mt-8">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== PUBLIC LINK PAGES (email buttons — no login required) ==============

const LinkPageShell = ({ children }) => (
  <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center">
    <div className="card-dark p-8 max-w-lg w-full text-center">{children}</div>
  </div>
);

const LinkNotFound = () => {
  const [orderNum, setOrderNum] = useState("");
  const [email, setEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null);
  const navigate = useNavigate();

  const handleLookup = async () => {
    if (!orderNum.trim() || !email.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/orders/lookup?order_number=${encodeURIComponent(orderNum.trim())}&email=${encodeURIComponent(email.trim())}`);
      if (res.data?.id) {
        navigate(`/orders/${res.data.id}`, { replace: true });
        return;
      }
      setFound(false);
    } catch { setFound(false); }
    setSearching(false);
  };

  return (
    <LinkPageShell>
      <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
      <h1 className="text-2xl font-bold mb-2">Link Not Found</h1>
      <p className="text-zinc-400 mb-6">This link is invalid or has expired.</p>
      <div className="border-t border-zinc-700 pt-4">
        <p className="text-zinc-500 text-sm mb-3">Look up your order:</p>
        <input type="text" value={orderNum} onChange={(e) => setOrderNum(e.target.value)} placeholder="Order number (e.g., S2M-20260306-1234)" className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white placeholder-zinc-500 text-sm mb-2" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email used for order" className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 text-white placeholder-zinc-500 text-sm mb-3" />
        <button onClick={handleLookup} disabled={searching} className="btn-primary w-full text-sm">{searching ? "Searching..." : "Find My Order"}</button>
        {found === false && <p className="text-red-400 text-xs mt-2">Order not found. Please check your details.</p>}
      </div>
    </LinkPageShell>
  );
};

const LinkOrderStatus = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const navigate = useNavigate();
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!code) { setErr(true); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/s/verify/${code}`);
        if (res.data.valid && res.data.order_id) {
          navigate(`/orders/${res.data.order_id}`, { replace: true });
        } else { setErr(true); }
      } catch { setErr(true); }
    })();
  }, [code, navigate]);

  if (err) return <LinkNotFound />;
  return <LinkPageShell><div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-4 text-zinc-400">Loading order...</p></LinkPageShell>;
};

const LinkInvoice = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [status, setStatus] = useState("loading");
  const [html, setHtml] = useState("");

  useEffect(() => {
    if (!code) { setStatus("error"); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/public/invoice?code=${code}`);
        setHtml(res.data);
        setStatus("ok");
      } catch { setStatus("error"); }
    })();
  }, [code]);

  if (status === "error") return <LinkNotFound />;
  if (status === "loading") return <LinkPageShell><div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-4 text-zinc-400">Loading invoice...</p></LinkPageShell>;
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const LinkEditorDownload = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!code) { setStatus("error"); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/public/download?code=${code}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a"); a.href = url; a.download = `photos_${code}.zip`;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
        setStatus("done");
      } catch { setStatus("error"); }
    })();
  }, [code]);

  if (status === "error") return <LinkNotFound />;
  if (status === "done") return <LinkPageShell><Check className="mx-auto mb-4 text-green-500" size={48} /><h1 className="text-2xl font-bold mb-2">Download Started</h1><p className="text-zinc-400">Your photos ZIP is downloading.</p></LinkPageShell>;
  return <LinkPageShell><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-4 text-zinc-400">Preparing photos...</p></LinkPageShell>;
};

const LinkEditorLogo = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [status, setStatus] = useState("loading");
  const [logoSrc, setLogoSrc] = useState(null);

  useEffect(() => {
    if (!code) { setStatus("error"); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/public/logo?code=${code}`, { responseType: "blob" });
        if (res.headers["content-type"]?.includes("text/html")) {
          setStatus("none");
        } else {
          setLogoSrc(window.URL.createObjectURL(new Blob([res.data])));
          setStatus("ok");
        }
      } catch { setStatus("error"); }
    })();
  }, [code]);

  if (status === "error") return <LinkNotFound />;
  if (status === "none") return <LinkPageShell><h1 className="text-2xl font-bold mb-2">No Agent Logo</h1><p className="text-zinc-400">A logo was not provided for this order.</p></LinkPageShell>;
  if (status === "ok" && logoSrc) return (
    <LinkPageShell>
      <h1 className="text-2xl font-bold mb-4">Agent Logo</h1>
      <img src={logoSrc} alt="Agent Logo" className="max-h-40 mx-auto mb-4" />
      <a href={logoSrc} download={`agent_logo_${code}.png`} className="btn-primary inline-block px-6 py-3">Download Logo</a>
    </LinkPageShell>
  );
  return <LinkPageShell><div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-4 text-zinc-400">Loading logo...</p></LinkPageShell>;
};

const LinkEditorUpload = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!code) { setStatus("error"); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/s/verify/${code}`);
        if (res.data.valid && res.data.action === "upload_video") {
          setOrder(res.data);
          setStatus("ready");
        } else { setStatus("error"); }
      } catch { setStatus("error"); }
    })();
  }, [code]);

  const handleUpload = async () => {
    if (!file || !code) return;
    setUploading(true); setMsg(""); setProgress(0);
    try {
      // 1) Init multipart upload
      setPhase("Initializing...");
      const initRes = await axios.post(`${API}/public/editor-upload-init`, {
        code, filename: file.name, fileSize: file.size, contentType: file.type || "video/mp4",
      });
      const { uploadId, objectKey, partSize, numParts } = initRes.data;

      // 2) Upload parts directly to R2 via presigned URLs (bypasses proxy)
      const parts = [];
      for (let i = 0; i < numParts; i++) {
        const partInfo = initRes.data.partUrls[i];
        const start = i * partSize;
        const end = Math.min(start + partSize, file.size);
        const chunk = file.slice(start, end);
        setPhase(`Part ${i + 1}/${numParts}`);

        // Try direct R2 presigned URL first
        let etag = null;
        try {
          const r2Res = await fetch(partInfo.url, { method: "PUT", body: chunk });
          if (r2Res.ok) {
            etag = r2Res.headers.get("etag");
          }
        } catch { /* CORS may block — fall through to proxy */ }

        // Fallback: proxy through backend if direct R2 fails
        if (!etag) {
          const proxyRes = await axios.put(
            `${API}/public/editor-upload-part/${uploadId}/${partInfo.partNumber}?code=${encodeURIComponent(code)}&key=${encodeURIComponent(objectKey)}`,
            chunk,
            { headers: { "Content-Type": "application/octet-stream" }, timeout: 300000 }
          );
          etag = proxyRes.data.ETag;
        }

        if (!etag) throw new Error(`Part ${i + 1} upload failed — no ETag returned`);
        parts.push({ PartNumber: partInfo.partNumber, ETag: etag });
        setProgress(Math.round(((i + 1) / numParts) * 95));
      }

      // 3) Complete
      setPhase("Finalizing...");
      setProgress(98);
      const completeRes = await axios.post(`${API}/public/editor-upload-complete`, {
        code, uploadId, objectKey, parts, originalName: file.name,
      });
      setProgress(100);
      // Store email result for display
      const emailOk = completeRes.data?.emailSuccess;
      const emailErr = completeRes.data?.emailError;
      if (emailOk) {
        setStatus("done");
      } else {
        setStatus("done_no_email");
        setMsg(emailErr || "Email notification may not have sent. Check with admin.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Upload failed";
      const httpStatus = e.response?.status || "network error";
      setMsg(`Error ${httpStatus}: ${detail}`);
      setUploading(false);
    }
  };

  if (status === "error") return <LinkNotFound />;
  if (status === "done") return <LinkPageShell><Check className="mx-auto mb-4 text-green-500" size={48} /><h1 className="text-2xl font-bold mb-2">Video Uploaded</h1><p className="text-zinc-400">The customer has been notified via email.</p></LinkPageShell>;
  if (status === "done_no_email") return (
    <LinkPageShell>
      <Check className="mx-auto mb-4 text-yellow-500" size={48} />
      <h1 className="text-2xl font-bold mb-2">Video Uploaded</h1>
      <p className="text-red-400 text-sm mb-2">Warning: Customer email notification may have failed.</p>
      <p className="text-zinc-500 text-xs mb-4">{msg}</p>
      <p className="text-zinc-400 text-sm">The video is saved. Please notify the customer manually or contact admin.</p>
    </LinkPageShell>
  );
  if (status === "loading") return <LinkPageShell><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-4 text-zinc-400">Verifying link...</p></LinkPageShell>;

  return (
    <LinkPageShell>
      <h1 className="text-2xl font-bold mb-2">Upload Finished Video</h1>
      <p className="text-zinc-400 text-sm mb-2">Order: {order?.order_id?.slice(0,8)}...</p>
      <p className="text-zinc-500 text-xs mb-6">Supports files up to 5GB. Uploads directly to cloud storage.</p>
      <label className="block border-2 border-dashed border-zinc-600 rounded-xl p-8 cursor-pointer hover:border-emerald-500 transition mb-4">
        <input type="file" accept="video/*" className="hidden" onChange={(e) => { setFile(e.target.files?.[0]); setMsg(""); }} />
        {file ? (
          <div>
            <p className="text-emerald-400 font-medium">{file.name}</p>
            <p className="text-zinc-500 text-xs mt-1">{(file.size / (1024*1024)).toFixed(1)} MB</p>
          </div>
        ) : <p className="text-zinc-500">Click to select video file</p>}
      </label>
      {uploading && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-zinc-400 mb-1">
            <span>{phase}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-3 bg-zinc-700 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      {msg && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-left"><p className="text-red-400 text-sm">{msg}</p><button onClick={() => { setMsg(""); setUploading(false); setProgress(0); }} className="text-xs text-zinc-400 underline mt-1">Retry</button></div>}
      <button onClick={handleUpload} disabled={!file || uploading} className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-bold px-8 py-3 rounded-lg w-full">
        {uploading ? "Uploading..." : "Upload Video"}
      </button>
    </LinkPageShell>
  );
};

// ============== MAIN APP ==============

// Scroll to top on route change - uses useLocation, not useParams
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

// Wrapper component to scroll to top on navigation
const ScrollToTopWrapper = ({ children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollRestoration />
        <div className="min-h-screen bg-[#0A0A0A] text-white">
          <Navbar />
          <Routes>
            {/* Umbrella Selector Page */}
            <Route path="/" element={<SelectorPage />} />
            
            {/* Memorial Routes */}
            <Route path="/memorial" element={<LandingPage />} />
            <Route path="/checkout" element={<VerticalGuard allowedVertical="memorial"><CheckoutPage /></VerticalGuard>} />
            
            {/* Real Estate Routes */}
            <Route path="/real-estate" element={<RealEstateLandingPage />} />
            <Route path="/real-estate/checkout" element={<VerticalGuard allowedVertical="real_estate"><RealEstateCheckoutPage /></VerticalGuard>} />
            
            {/* Shared Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/thank-you/:orderId" element={<ThankYouPage />} />
            <Route path="/editor-upload" element={<EditorUploadPage />} />
            <Route path="/download/:code" element={<SecureDownloadPage />} />
            <Route path="/go/order-status" element={<LinkOrderStatus />} />
            <Route path="/go/editor-download" element={<LinkEditorDownload />} />
            <Route path="/go/editor-logo" element={<LinkEditorLogo />} />
            <Route path="/go/editor-upload" element={<LinkEditorUpload />} />
            <Route path="/go/invoice" element={<LinkInvoice />} />
          </Routes>
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Scroll restoration component
const ScrollRestoration = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
};

export default App;
